import {
  Transaction,
  Split,
  Account,
  ImportedEntry,
  ExternalAccount
} from "../model/bookkeeping";
import { UUID } from "../lib/core/uuid";
import { BackupDataSchema } from "./backup";
import { TransactionMutations } from "data/accounts/types";

export abstract class Persist {
  private newAccountHandler: (() => void) | undefined = undefined;
  private onRemoteChangeHandlers = new Set<() => void>();

  registerNewAccountHandler(handler: () => void) {
    this.newAccountHandler = handler;
  }

  runNewAccountHandler() {
    if (this.newAccountHandler) {
      this.newAccountHandler();
    }
  }

  addOnRemoteChangeListener(listener: () => void) {
    this.onRemoteChangeHandlers.add(listener);
  }

  removeOnRemoteChangeListener(listener: () => void) {
    this.onRemoteChangeHandlers.delete(listener);
  }

  protected onChange() {
    this.onRemoteChangeHandlers.forEach(f => f());
  }

  abstract listenForChange(): void;

  abstract addTransactions(
    transactions: Transaction[],
    splits: Split[]
  ): Promise<void>;

  abstract getAccountData(): Promise<{
    accounts: Account[];
    transactions: Transaction[];
    splits: Split[];
  }>;

  abstract addAccounts(accounts: Account[]): Promise<void>;

  abstract updateAccount(id: UUID, name: string): Promise<void>;

  abstract deleteAccounts(
    accounts: {
      accountId: UUID;
      moveTo: UUID;
    }[]
  ): Promise<void>;

  abstract moveAllSplitsInAccount(
    accounts: {
      accountId: UUID;
      moveTo: UUID;
    }[]
  ): Promise<void>;

  abstract getImporterData(): Promise<{
    importedEntries: ImportedEntry[];
    externalAccounts: ExternalAccount[];
  }>;

  abstract updateImportedEntrySplitIds(mapping: Map<UUID, UUID>): Promise<void>;

  abstract addExternalAccountsAndEntries(
    externalAccount: ExternalAccount | undefined,
    entries: ImportedEntry[]
  ): Promise<UUID[]>;

  abstract updateExternalAccount(
    externalAccount: Partial<ExternalAccount> & Pick<ExternalAccount, "id">
  ): Promise<void>;

  abstract updateExternalAccountTags(
    externalAccountId: string,
    tags: { [tag: string]: string }
  ): Promise<void>;

  abstract updateImportedEntryTags(
    id: string,
    tags: { [tag: string]: string }
  ): Promise<void>;

  abstract updateAccountTags(
    accountId: string,
    tags: { [tag: string]: string }
  ): Promise<void>;

  abstract updateTransactionsAndSplits(
    mutations: TransactionMutations
  ): Promise<void>;

  abstract backup(): Promise<BackupDataSchema>;

  abstract restore(data: BackupDataSchema): Promise<void>;
}
